import { Injectable, inject } from '@angular/core';
import { map, Observable, of, shareReplay, switchMap, take } from 'rxjs';
import type { AssistantIntentResult } from '../models/assistant-intent.model';
import { AssistantConversationStateService } from './assistant-conversation-state.service';
import { EducationalWorkflowConfigService } from '../educational-workflow-config.service';
import { normalizeSlotText } from '../utils/slot-normalizer.util';
import { GREETING_RE } from '../utils/assistant-conversation-patterns.util';
import { logAssistantDebug } from '../utils/assistant-debug.util';
import {
  AssistantWorkflowContinuationService,
  GENERAL_CHAT_HINTS,
  type WorkflowContinuationDecision,
} from './assistant-workflow-continuation.service';

const EDU_VERBS = /\b(create|make|generate|build|write|compose|give me|i want|i need|help me)\b/i;

@Injectable({
  providedIn: 'root',
})
export class AssistantIntentService {
  private readonly conv = inject(AssistantConversationStateService);
  private readonly config = inject(EducationalWorkflowConfigService);
  private readonly continuation = inject(AssistantWorkflowContinuationService);

  private readonly keywordMap$ = this.config.ensureLoaded().pipe(
    map(bundle => {
      const mapByArtifact = new Map<string, string[]>();
      for (const [key, art] of Object.entries(bundle.artifacts)) {
        const set = new Set<string>();
        for (const w of [...(art.intents ?? []), ...(art.aliases ?? []), key]) {
          const n = normalizeSlotText(w);
          if (n) set.add(n);
        }
        mapByArtifact.set(key, [...set]);
      }
      return mapByArtifact;
    }),
    shareReplay(1)
  );

  classify(message: string, registryMatchedNavigation: boolean): Observable<AssistantIntentResult> {
    const trimmed = message.trim();
    if (!trimmed) {
      return new Observable(sub => {
        sub.next({ kind: 'unknown' });
        sub.complete();
      });
    }

    if (registryMatchedNavigation) {
      return new Observable(sub => {
        sub.next({ kind: 'navigation' });
        sub.complete();
      });
    }

    return this.keywordMap$.pipe(
      take(1),
      switchMap(keywordMap => {
        if (this.conv.isCancelPhrase(trimmed)) {
          return of({ kind: 'session_end' } as const);
        }

        if (this.conv.workflowSessionActive()) {
          const artifact = this.conv.sessionArtifact();
          if (!artifact) {
            return of({ kind: 'workflow_continue' } as const);
          }
          return this.config.getMergedArtifactConfig(artifact).pipe(
            take(1),
            map(merged =>
              this.mapContinuationDecision(
                this.continuation.evaluate(trimmed, keywordMap, merged),
                trimmed
              )
            )
          );
        }

        return of(this.classifyWithoutSession(trimmed, keywordMap));
      })
    );
  }

  private classifyWithoutSession(
    trimmed: string,
    keywordMap: Map<string, string[]>
  ): AssistantIntentResult {
    const t = normalizeSlotText(trimmed);

    if (GREETING_RE.test(trimmed)) {
      return { kind: 'general_chat' };
    }

    if (GENERAL_CHAT_HINTS.test(trimmed)) {
      return { kind: 'general_chat' };
    }

    const artifactHint = this.pickArtifactFromKeywords(t, keywordMap);
    if (artifactHint) {
      return { kind: 'educational_generation', artifactHint };
    }
    if (EDU_VERBS.test(trimmed) && this.matchesAnyArtifactKeyword(t, keywordMap)) {
      const inferred = this.pickArtifactFromKeywords(t, keywordMap);
      if (inferred) {
        return { kind: 'educational_generation', artifactHint: inferred };
      }
    }

    return { kind: 'general_chat' };
  }

  private mapContinuationDecision(
    decision: WorkflowContinuationDecision,
    trimmed: string
  ): AssistantIntentResult {
    logAssistantDebug('DETECTED_INTENT', {
      message: trimmed,
      continuation: decision,
      artifact: this.conv.sessionArtifact(),
      slot: this.conv.currentSlotKey(),
      generationPathMode: this.conv.generationPathMode(),
    });

    switch (decision.action) {
      case 'session_end':
        return { kind: 'session_end' };
      case 'interrupt':
        if (decision.reason === 'new_artifact' && decision.artifactHint) {
          return {
            kind: 'educational_generation',
            artifactHint: decision.artifactHint,
          };
        }
        return { kind: 'general_chat' };
      case 'enrich':
        return {
          kind: 'workflow_continue',
          workflowEnrichment: {
            pathHint: decision.pathHint,
            topicHint: decision.topicHint,
            patch: decision.patch,
          },
        };
      case 'continue':
      default:
        return { kind: 'workflow_continue' };
    }
  }

  private matchesAnyArtifactKeyword(t: string, map: Map<string, string[]>): boolean {
    for (const words of map.values()) {
      for (const w of words) {
        if (w.length >= 3 && (t.includes(w) || w.includes(t))) return true;
      }
    }
    return false;
  }

  private pickArtifactFromKeywords(t: string, map: Map<string, string[]>): string | null {
    let best: string | null = null;
    let bestLen = 0;
    for (const [artifact, words] of map) {
      for (const w of words) {
        if (w.length >= 3 && (t.includes(w) || w.includes(t)) && w.length >= bestLen) {
          best = artifact;
          bestLen = w.length;
        }
      }
    }
    return best;
  }
}
